import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import BlockService, { ContentBlock } from '../../services/blockService';
import BlockEditor from './BlockEditor';
import { useBlockManager } from '../../hooks/useBlockManager';
import { BlockContainer } from '../../services/blockManager';
import ImageLightbox from './ImageLightbox';
import './BlockPage.css';

interface BlockPageProps {
  user: User | null;
  onToggleSidebar: () => void;
}

// Interface for the blocks field structure
interface BlocksStructure {
  block_ids: string[];
}

// Enhanced ContentBlock interface for parent block display
interface EnhancedContentBlock extends ContentBlock {
  children?: ContentBlock[];
  blocks_data?: BlocksStructure;
}

const BlockPage: React.FC<BlockPageProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { block_id } = useParams<{ block_id?: string }>();
  const [currentBlock, setCurrentBlock] = useState<EnhancedContentBlock | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullBlockMode, setIsFullBlockMode] = useState(false);
  const [lightboxState, setLightboxState] = useState<{
    isVisible: boolean;
    imageUrl: string;
    metadata?: { originalName?: string; width?: number; height?: number };
  }>({
    isVisible: false,
    imageUrl: ''
  });

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Create a ref to hold the current block for the callback
  const currentBlockRef = useRef<EnhancedContentBlock | null>(null);
  
  // Update the ref whenever currentBlock changes
  useEffect(() => {
    console.log('🔄 BlockPage::useEffect::currentBlockRef::Updating currentBlockRef:', currentBlock, 'user ', user);
    currentBlockRef.current = currentBlock;
  }, [currentBlock]);

  // Block management for children blocks using standardized hook
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'block',
    containerId: block_id || '', // Use block_id as container ID
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const currentBlockValue = currentBlockRef.current;
      
      console.log('🔄 BlockPage::onContainerUpdate called:', {
        containerBlocks: container.blocks,
        currentBlockId: currentBlockValue?.block_id
      });

      if (currentBlockValue) {
        // Update the blocks array in the parent block
        await updateChildrenBlocks(currentBlockValue.block_id, container.blocks);
      }
    }
  });

  // Load the parent block
  const loadParentBlock = async (blockId: string) => {
    console.log('🔄 BlockPage::loadParentBlock::Loading block:', blockId, 'user ', user);

    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const block = await BlockService.getBlockById(blockId);
      
      if (block) {
        // Parse blocks field to get children block IDs
        let blocksData: BlocksStructure;
        
        try {
          // Handle blocks field - it can be a string array or null
          if (block.blocks && Array.isArray(block.blocks)) {
            blocksData = { block_ids: block.blocks };
          } else {
            blocksData = { block_ids: [] };
          }
        } catch (parseError) {
          console.warn('⚠️ BlockPage::loadParentBlock::Failed to parse blocks field:', parseError);
          blocksData = { block_ids: [] };
        }
        
        const enhancedBlock: EnhancedContentBlock = {
          ...block,
          blocks_data: blocksData
        };
        
        setCurrentBlock(enhancedBlock);
        
        // Load children blocks based on blocks array
        await loadChildrenBlocks(blockId);
      } else {
        setError('Block not found');
        setCurrentBlock(null);
      }
    } catch (error: any) {
      console.error('loadParentBlock::Error loading block:', error);
      setError('Failed to load block');
      setCurrentBlock(null);
    } finally {
      setLoading(false);
    }
  };

  // Load children blocks using BlockService.getChildBlocks
  const loadChildrenBlocks = async (parentBlockId: string) => {
    try {
      // Use BlockService to get child blocks
      const childrenResponse = await BlockService.getChildBlocks(parentBlockId);
      
      if (childrenResponse.success && childrenResponse.data.children_blocks.length > 0) {
        // Extract child block IDs from the response
        const childBlockIds = childrenResponse.data.children_blocks.map((block: any) => block.block_id);
        console.log('📋 BlockPage::loadChildrenBlocks::Loading children blocks:', childBlockIds);
        setContainerId(parentBlockId);
        await loadBlocks(childBlockIds);
      } else {
        // No children, clear the container
        console.log('📋 BlockPage::loadChildrenBlocks::No children blocks found');
        setContainerId(parentBlockId);
        await loadBlocks([]);
      }
    } catch (error) {
      console.error('Error loading children blocks:', error);
      setError('Failed to load children blocks');
    }
  };

  // Update children blocks array in the parent block
  const updateChildrenBlocks = async (parentBlockId: string, childBlockIds: string[]) => {
    try {
      // Get the current parent block
      const currentParentBlock = await BlockService.getBlockById(parentBlockId);
      
      if (currentParentBlock) {
        // Update the block with the new children blocks array
        await BlockService.updateBlock(parentBlockId, {
          content_type: currentParentBlock.content_type,
          text_value: currentParentBlock.text_value,
          blocks: childBlockIds
        });

        console.log('✅ BlockPage::updateChildrenBlocks::Updated blocks array for parent:', parentBlockId, 'with children:', childBlockIds);
      }
    } catch (error) {
      console.error('Error updating children blocks:', error);
      throw error;
    }
  };

  // Handle opening lightbox
  const handleOpenLightbox = useCallback((imageUrl: string, metadata?: { originalName?: string; width?: number; height?: number }) => {
    setLightboxState({
      isVisible: true,
      imageUrl,
      metadata
    });
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxState({
      isVisible: false,
      imageUrl: ''
    });
  }, []);

  // Render parent block content (handles image blocks specially)
  const renderParentBlockContent = () => {
    if (!currentBlock) return null;

    // Check if this is an image block
    if (currentBlock.content_type === 'image') {
      const imageUrl = currentBlock.text_value || '';
      
      // Try to parse metadata from properties
      let imageMetadata: { originalName?: string; width?: number; height?: number } | undefined;
      try {
        const properties = currentBlock.properties;
        if (properties?.imageMetadata) {
          imageMetadata = properties.imageMetadata;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      if (imageUrl) {
        return (
          <div className="parent-block-image">
            <img
              src={imageUrl}
              alt={imageMetadata?.originalName || 'Image'}
              className="block-image-thumbnail"
              onClick={() => handleOpenLightbox(imageUrl, imageMetadata)}
              onError={(e) => {
                // Show error state if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.className = 'block-image-error';
                errorDiv.textContent = 'Failed to load image';
                target.parentElement?.appendChild(errorDiv);
              }}
              title="Click to view full size"
            />
          </div>
        );
      } else {
        return (
          <div className="parent-block-image-placeholder">
            No image URL
          </div>
        );
      }
    }

    // Default: render as text
    return (
      <div className="parent-block-text">
        {currentBlock.text_value}
      </div>
    );
  };


  // Load data when block_id changes
  useEffect(() => {
    console.log('🔄 BlockPage::Loading data for block:', block_id, 'user:', user?.id);

    if (user && block_id) {
      loadParentBlock(block_id);
    }
  }, [user?.id, block_id]);


  if (!user) {
    return (
      <div className="block-page-container">
        <div className="block-page-header">
          <div className="header-left">
            <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
            <button 
              className="back-btn"
              onClick={() => navigate(-1)}
              title="Go back"
            >
              ← Back
            </button>
          </div>
          <div>Block View</div>
          <div className="header-actions">
          </div>
        </div>
        <div className="auth-required">
          <p>Please sign in to access blocks.</p>
        </div>
      </div>
    );
  }

  if (!block_id) {
    return (
      <div className="block-page-container">
        <div className="block-page-header">
          <div className="header-left">
            <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
            <button 
              className="back-btn"
              onClick={() => navigate(-1)}
              title="Go back"
            >
              ← Back
            </button>
          </div>
          <div>Block View</div>
          <div className="header-actions">
          </div>
        </div>
        <div className="error-message">
          <span>No block ID provided</span>
        </div>
      </div>
    );
  }

  return (
    <div className="block-page-container">
      <div className="block-page-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          <button 
            className="back-btn"
            onClick={() => navigate(-1)}
            title="Go back"
          >
            ←
          </button>
        </div>
        <div className="header-title">
          
        </div>
        <div className="header-actions">
          <button
            className="block-page-mode-btn"
            onClick={() => setIsFullBlockMode(!isFullBlockMode)}
            title={`Full Block Mode: ${isFullBlockMode ? 'ON' : 'OFF'}`}
          >
            Full Block: {isFullBlockMode ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}


      <div className="block-page-content">
        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading block...</p>
          </div>
        ) : currentBlock ? (
          <>
            {/* Parent Block Content */}
            <div className="parent-block-content">
              {renderParentBlockContent()}
            </div>

            {/* Block Editor for children */}
            <div className="block-editor">
              {blocksLoading ? (
                <div className="children-loading">Loading children blocks...</div>
              ) : (
                <BlockEditor
                  blockManager={blockManager}
                  blocks={contentBlocks}
                  placeholder="Add child blocks to organize related content..."
                  onSyncStatusChange={(status: any) => {}} // Sync status is handled by hook
                  fullBlockMode={isFullBlockMode}
                />
              )}
            </div>

            {/* Image Lightbox */}
            {lightboxState.isVisible && (
              <ImageLightbox
                imageUrl={lightboxState.imageUrl}
                onClose={handleCloseLightbox}
                metadata={lightboxState.metadata}
              />
            )}
          </>
        ) : (
          <div className="block-not-found">
            <p>Block not found or you don't have permission to view it.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockPage;
